import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Button,
  Typography,
  Avatar,
  Collapse,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  ImageOutlined as ImageOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  SwapVert as SwapVertIcon,
} from '@mui/icons-material';
import CategorySortModal from '../../../components/PosMenu/CategorySortModal';
import { setSelectedEntity } from '../../../redux/actions/posMenu';
import {
  getTranslationValue,
  SOLD_OUT_STATUS,
} from '../../../constants/posMenu';
import { filteringSoldOut } from '../../../utils/soldOut';
import { sortCategories } from '../../../utils/posMenuCategories';
import escapeRegExp from 'lodash/escapeRegExp';

const HighlightedText = ({ text, highlight }) => {
  if (!highlight.trim()) return text;

  const parts = text.split(new RegExp(`(${escapeRegExp(highlight)})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <span key={i} style={{ backgroundColor: '#fff59d', fontWeight: 600 }}>
            {part}
          </span>
        ) : (
          part
        )
      )}
    </>
  );
};

// Status indicator dot component
const StatusDot = ({ status }) => {
  let bgcolor = '#4caf50'; // green for available
  let tooltip = 'Available';

  if (status === SOLD_OUT_STATUS.SOLD_OUT_TODAY) {
    bgcolor = '#ff9800'; // orange for sold out today
    tooltip = 'Sold Out Today';
  } else if (status === SOLD_OUT_STATUS.SOLD_OUT_PERMANENT) {
    bgcolor = '#f44336'; // red for permanently unavailable
    tooltip = 'Permanently Unavailable';
  }

  return (
    <Tooltip title={tooltip} placement="top">
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor,
          flexShrink: 0,
        }}
      />
    </Tooltip>
  );
};

const MenuTree = ({ menuMap, selectedEntity, selectedEntityType }) => {
  const dispatch = useDispatch();
  const currentBusiness = useSelector(
    (state) => state.business?.currentBusiness
  );
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [categorySortModalOpen, setCategorySortModalOpen] = useState(false);

  // Track if initial expansion has been set and the last category keys
  const hasInitializedRef = useRef(false);
  const lastCategoryKeysRef = useRef('');

  // Initialize all categories as expanded when categories change or search query changes
  // Use category keys as dependency instead of entire menuMap to avoid unnecessary resets
  useEffect(() => {
    if (menuMap?.categories) {
      const categoryKeys = Object.keys(menuMap.categories).sort().join(',');
      
      if (searchQuery.trim()) {
        // When searching, expand all categories that match
        const allCats = Object.values(menuMap.categories);
        const query = searchQuery.toLowerCase();

        const searchExpanded = {};
        allCats.forEach((category) => {
          const categoryName = getTranslationValue(category.name).toLowerCase();
          const categoryMatches = categoryName.includes(query);

          // Check if any item in this category matches (by name or internal name)
          const hasMatchingItem = (category.itemIds || []).some((itemId) => {
            const item = menuMap.items?.[itemId];
            if (!item) return false;
            const itemName = getTranslationValue(item.name).toLowerCase();
            const itemTitle = getTranslationValue(item.title)?.toLowerCase() || '';
            return itemName.includes(query) || itemTitle.includes(query);
          });

          // Expand if category matches or has matching items
          if (categoryMatches || hasMatchingItem) {
            searchExpanded[category._id] = true;
          }
        });
        setExpandedCategories(searchExpanded);
      } else {
        // When not searching, only expand all categories on:
        // 1. Initial load (hasInitializedRef is false)
        // 2. Category structure changes (categoryKeys changed)
        const shouldInitialize = !hasInitializedRef.current || lastCategoryKeysRef.current !== categoryKeys;
        
        if (shouldInitialize) {
          const allExpanded = {};
          Object.keys(menuMap.categories).forEach((categoryId) => {
            allExpanded[categoryId] = true;
          });
          setExpandedCategories(allExpanded);
          hasInitializedRef.current = true;
          lastCategoryKeysRef.current = categoryKeys;
        }
      }
    }
  }, [menuMap?.categories, searchQuery]);

  const handleToggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleSelectCategory = (category) => {
    // Don't add to history when selecting from tree - skipHistory = true
    dispatch(setSelectedEntity(category, 'category', true));
  };

  const handleSelectItem = (item) => {
    // Don't add to history when selecting from tree - skipHistory = true
    dispatch(setSelectedEntity(item, 'item', true));
  };

  const handleAddCategory = () => {
    dispatch(setSelectedEntity(null, 'category', true));
  };

  const handleAddItem = (categoryId) => {
    dispatch(setSelectedEntity({ categoryId }, 'item', true));
  };

  if (!menuMap) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="textSecondary">
          No menu data available
        </Typography>
      </Box>
    );
  }

  const allCategories = sortCategories(Object.values(menuMap.categories || {}));

  // Filter categories and items based on search query
  const categories = allCategories.filter((category) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    const categoryName = getTranslationValue(category.name).toLowerCase();

    // Check if category name matches
    if (categoryName.includes(query)) return true;

    // Check if any item in this category matches (by name or internal name)
    const items = (category.itemIds || [])
      .map((itemId) => menuMap.items?.[itemId])
      .filter(Boolean);

    const hasMatchingItem = items.some((item) => {
      const itemName = getTranslationValue(item.name).toLowerCase();
      const itemTitle = getTranslationValue(item.title)?.toLowerCase() || '';
      return itemName.includes(query) || itemTitle.includes(query);
    });

    return hasMatchingItem;
  });

  const renderItemRow = (item) => {
    const isSelected =
      selectedEntityType === 'item' && selectedEntity?._id === item._id;
    return (
      <ListItem key={item._id} disablePadding>
        <ListItemButton
          sx={{
            pl: 8,
            background: 'white',
            bgcolor: isSelected ? 'rgba(158, 158, 173, 0.08)' : 'white',
            '&:hover': {
              bgcolor: 'rgba(158, 158, 173, 0.04)',
            },
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1.5,
          }}
          onClick={() => handleSelectItem(item)}
        >
          {item?.image?.url ? (
            <Avatar
              sx={{
                bgcolor: '#E2E2E2',
                width: 48,
                height: 48,
              }}
              alt={getTranslationValue(item.name)}
              src={`${item.image.url}?tr=w-48,h-48`}
            />
          ) : (
            <Avatar
              sx={{
                bgcolor: '#E2E2E2',
                width: 48,
                height: 48,
              }}
            >
              <ImageOutlinedIcon
                sx={{
                  color: '#323232',
                  fontSize: 20,
                }}
              />
            </Avatar>
          )}
          <ListItemText
            primary={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <StatusDot
                  status={
                    filteringSoldOut(item, currentBusiness) ||
                    SOLD_OUT_STATUS.AVAILABLE
                  }
                />
                <HighlightedText
                  text={getTranslationValue(item.name)}
                  highlight={searchQuery}
                />
              </Box>
            }
            secondary={`$${(Number(item.price) || 0).toFixed(2)}`}
            primaryTypographyProps={{
              fontSize: '14px',
              fontWeight: isSelected ? 600 : 500,
              color: '#141414',
            }}
            secondaryTypographyProps={{
              fontSize: '12px',
              color: '#434343',
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Box sx={{ p: 2, bgcolor: '#fafafa', height: '100%' }}>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: '16px',
            fontWeight: 600,
            color: '#141414',
          }}
        >
          POS Menu
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<SwapVertIcon />}
            onClick={() => setCategorySortModalOpen(true)}
            disabled={allCategories.length < 2}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
            }}
          >
            Sort
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddCategory}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
            }}
          >
            Add Category
          </Button>
        </Box>
      </Box>

      <TextField
        fullWidth
        size="small"
        placeholder="Search categories and items..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: searchQuery && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearchQuery('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 2,
          bgcolor: 'white',
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: 'primary.main',
            },
          },
        }}
      />

      <List dense>
        {categories.length === 0 ? (
          searchQuery.trim() ? (
            <ListItem>
              <ListItemText
                primary="No results"
                secondary={`Nothing matches "${searchQuery.trim()}"`}
              />
            </ListItem>
          ) : (
            <ListItem>
              <ListItemText
                primary="No categories yet"
                secondary="Click 'Add Category' to get started"
              />
            </ListItem>
          )
        ) : (
          categories.map((category) => {
            const categoryId = category._id;
            // Get items from itemIds array - order is determined by itemIds position
            const allItems = (category.itemIds || [])
              .map((itemId) => menuMap.items?.[itemId])
              .filter(Boolean);

            // Filter items based on search query (by name or internal name)
            const items = searchQuery.trim()
              ? allItems.filter((item) => {
                  const itemName = getTranslationValue(item.name).toLowerCase();
                  const itemTitle = getTranslationValue(item.title)?.toLowerCase() || '';
                  const query = searchQuery.toLowerCase();
                  return itemName.includes(query) || itemTitle.includes(query);
                })
              : allItems;

            const isExpanded = expandedCategories[categoryId];

            return (
              <Box key={categoryId}>
                <ListItem
                  disablePadding
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleAddItem(categoryId)}
                      title="Add Item"
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <IconButton
                    size="small"
                    onClick={() => handleToggleCategory(categoryId)}
                    sx={{ ml: 1 }}
                  >
                    {isExpanded ? (
                      <ExpandMoreIcon fontSize="small" />
                    ) : (
                      <ChevronRightIcon fontSize="small" />
                    )}
                  </IconButton>
                  <ListItemButton
                    onClick={() => handleSelectCategory(category)}
                    sx={{
                      background: 'white',
                      bgcolor:
                        selectedEntityType === 'category' &&
                        selectedEntity?._id === categoryId
                          ? 'rgba(158, 158, 173, 0.08)'
                          : 'white',
                      '&:hover': {
                        bgcolor: 'rgba(158, 158, 173, 0.04)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <HighlightedText
                          text={getTranslationValue(category.name)}
                          highlight={searchQuery}
                        />
                      }
                      secondary={`${items.length} items`}
                      primaryTypographyProps={{
                        fontWeight:
                          selectedEntityType === 'category' &&
                          selectedEntity?._id === categoryId
                            ? 600
                            : 500,
                        fontSize: '14px',
                        color: '#141414',
                      }}
                      secondaryTypographyProps={{
                        fontSize: '12px',
                        color: '#9E9EAD',
                      }}
                    />
                  </ListItemButton>
                </ListItem>

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {items.length === 0 ? (
                      <ListItem sx={{ pl: 8 }}>
                        <ListItemText
                          primary="No items"
                          secondary="Click + to add an item"
                          primaryTypographyProps={{
                            variant: 'body2',
                            color: 'textSecondary',
                          }}
                        />
                      </ListItem>
                    ) : (
                      items.map(renderItemRow)
                    )}
                  </List>
                </Collapse>
              </Box>
            );
          })
        )}
      </List>

      {/* Category Sort Modal */}
      <CategorySortModal
        open={categorySortModalOpen}
        onClose={() => setCategorySortModalOpen(false)}
        categories={allCategories}
        menuMap={menuMap}
      />
    </Box>
  );
};

export default MenuTree;
